import { CheckIcon, ChevronDownIcon, SlidersHorizontal, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { FilterPopover } from "@/components/dashboard/resources-filters/FilterPopover";
import {
  ChipRow,
  ChipToggle,
  FilterSection,
} from "@/components/dashboard/resources-filters/filter-controls";
import { SearchInput } from "@/components/shared/search-input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { addMonths, monthLabel, monthRange } from "@/lib/availability/months";
import {
  type AvailabilityView,
  GROUP_OPTIONS,
  SORT_OPTIONS,
  WINDOW_PRESETS,
} from "./search-params";

const SEARCH_DEBOUNCE_MS = 250;

/** Months offered in the Custom From / To lists: a year back to two years ahead. */
const CUSTOM_MONTHS_BACK = 12;
const CUSTOM_MONTHS_SPAN = 37;

/** The option whose value matches, so a Select's string comes back as the typed union. */
function findValue<T extends string>(
  options: ReadonlyArray<{ value: T }>,
  value: string,
): T | undefined {
  return options.find((option) => option.value === value)?.value;
}

interface SearchBoxProps {
  initial: string;
  onCommit: (q: string) => void;
}

/** Search-first. Keeps its own text so typing never waits on the URL; commits after a pause. */
function SearchBox({ initial, onCommit }: SearchBoxProps) {
  const [draft, setDraft] = useState(initial);
  const debounced = useDebouncedValue(draft, SEARCH_DEBOUNCE_MS);
  const committed = useRef(initial);
  const commit = useRef(onCommit);
  commit.current = onCommit;

  useEffect(() => {
    if (debounced === committed.current) return;
    committed.current = debounced;
    commit.current(debounced);
  }, [debounced]);

  return (
    <SearchInput
      value={draft}
      onValueChange={setDraft}
      placeholder="Search Resources…"
      className="w-56"
    />
  );
}

function ToggleBox({
  id,
  label,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={(next) => onChange(next === true)} />
      <Label htmlFor={id} className="cursor-pointer text-sm font-normal">
        {label}
      </Label>
    </div>
  );
}

interface Chip {
  key: string;
  label: string;
  clear: () => void;
}

/** The row filters that live behind Filters, as removable chips when they differ from default. */
function filterChips(
  view: AvailabilityView,
  onChange: (patch: Partial<AvailabilityView>) => void,
): Chip[] {
  const chips: Chip[] = [];
  if (view.exceptionsOnly)
    chips.push({
      key: "exc",
      label: "Exceptions only",
      clear: () => onChange({ exceptionsOnly: false }),
    });
  if (!view.includeProposed)
    chips.push({
      key: "prop",
      label: "Without proposed",
      clear: () => onChange({ includeProposed: true }),
    });
  if (!view.deliveryOnly)
    chips.push({
      key: "deliv",
      label: "All Home Roles",
      clear: () => onChange({ deliveryOnly: true }),
    });
  if (view.showActual)
    chips.push({
      key: "act",
      label: "Actual months shown",
      clear: () => onChange({ showActual: false }),
    });
  return chips;
}

interface TimePeriodPickerProps {
  view: AvailabilityView;
  onChange: (patch: Partial<AvailabilityView>) => void;
  windowLabel: string;
  rangeLabel: string;
  windowEnds: { from: string; to: string } | null;
  customMonths: string[];
}

/**
 * Time period as one dropdown: the presets, and for Custom the From / To months inside
 * the same panel, so a custom range never pushes the toolbar onto a second line.
 */
function TimePeriodPicker({
  view,
  onChange,
  windowLabel,
  rangeLabel,
  windowEnds,
  customMonths,
}: TimePeriodPickerProps) {
  const [open, setOpen] = useState(false);
  const isCustom = view.preset === "custom";
  const label = isCustom
    ? windowLabel
    : (WINDOW_PRESETS.find((preset) => preset.value === view.preset)?.label ?? windowLabel);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-label="Time period"
        title={rangeLabel}
        className="flex h-9 w-48 items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 text-sm whitespace-nowrap shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30"
      >
        <span className="truncate">{label}</span>
        <ChevronDownIcon aria-hidden className="size-4 shrink-0 opacity-50" />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 gap-0 p-1">
        <div role="listbox" aria-label="Time period">
          {WINDOW_PRESETS.map((preset) => {
            const active = view.preset === preset.value;
            return (
              <button
                key={preset.value}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => {
                  if (preset.value === "custom") {
                    if (!isCustom && windowEnds !== null)
                      onChange({ preset: "custom", from: windowEnds.from, to: windowEnds.to });
                    return;
                  }
                  onChange({ preset: preset.value });
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
              >
                {preset.label}
                {active ? <CheckIcon aria-hidden className="size-4" /> : null}
              </button>
            );
          })}
        </div>
        {isCustom ? (
          <div className="mt-1 flex items-center gap-1.5 border-t px-1 pt-2 pb-1 text-sm text-muted-foreground">
            <Select
              value={view.from ?? undefined}
              onValueChange={(from) => onChange({ preset: "custom", from })}
            >
              <SelectTrigger aria-label="From month" size="sm" className="flex-1">
                <SelectValue placeholder="From" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {customMonths.map((month) => (
                  <SelectItem key={month} value={month}>
                    {monthLabel(month)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            to
            <Select
              value={view.to ?? undefined}
              onValueChange={(to) => onChange({ preset: "custom", to })}
            >
              <SelectTrigger aria-label="To month" size="sm" className="flex-1">
                <SelectValue placeholder="To" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {customMonths.map((month) => (
                  <SelectItem key={month} value={month}>
                    {monthLabel(month)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

interface AvailabilityControlsProps {
  view: AvailabilityView;
  onChange: (patch: Partial<AvailabilityView>) => void;
  /** The resolved window: "Oct 2026 – Dec 2026". Names a Custom period on its trigger. */
  windowLabel: string;
  /** The same with its length: "Oct 2026 – Dec 2026 · 3 months". Shown on hover. */
  rangeLabel: string;
  /** First and last month of the resolved window, used to seed Custom. */
  windowEnds: { from: string; to: string } | null;
  /** Today's month, to centre the Custom month lists. */
  currentMonth: string;
  /** Why a Custom range was refused, if it was. */
  windowProblem: string | null;
  allOpen: boolean;
  /** No group header rows to collapse when grouping is off. */
  canCollapse: boolean;
  onToggleAll: () => void;
}

/**
 * One row: search, Time period, Group by, Sort, then Filters for everything else.
 * Applied filters show as chips beneath, the same as every other list in the app.
 */
export function AvailabilityControls({
  view,
  onChange,
  windowLabel,
  rangeLabel,
  windowEnds,
  currentMonth,
  windowProblem,
  allOpen,
  canCollapse,
  onToggleAll,
}: AvailabilityControlsProps) {
  const customMonths = monthRange(addMonths(currentMonth, -CUSTOM_MONTHS_BACK), CUSTOM_MONTHS_SPAN);
  const chips = filterChips(view, onChange);
  const count = chips.length;
  const clearAll = () =>
    onChange({
      exceptionsOnly: false,
      includeProposed: true,
      deliveryOnly: true,
      showActual: false,
    });

  return (
    <section aria-label="Report controls" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <SearchBox initial={view.q} onCommit={(q) => onChange({ q })} />

        <TimePeriodPicker
          view={view}
          onChange={onChange}
          windowLabel={windowLabel}
          rangeLabel={rangeLabel}
          windowEnds={windowEnds}
          customMonths={customMonths}
        />

        <Select
          value={view.groupBy}
          onValueChange={(next) => {
            const groupBy = findValue(GROUP_OPTIONS, next);
            if (groupBy !== undefined) onChange({ groupBy });
          }}
        >
          <SelectTrigger aria-label="Group by">
            <span className="text-muted-foreground">Group by</span>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GROUP_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={view.sort}
          onValueChange={(next) => {
            const sort = findValue(SORT_OPTIONS, next);
            if (sort !== undefined) onChange({ sort });
          }}
        >
          <SelectTrigger aria-label="Sort">
            <span className="text-muted-foreground">Sort</span>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <FilterPopover
          trigger={() => (
            <>
              <SlidersHorizontal className="size-4" />
              <span>Filters{count > 0 ? ` (${count})` : ""}</span>
            </>
          )}
        >
          {() => (
            <div>
              <FilterSection label="Rows">
                <div className="flex flex-col gap-2.5">
                  <ToggleBox
                    id="availability-exceptions"
                    label="Exceptions only"
                    checked={view.exceptionsOnly}
                    onChange={(exceptionsOnly) => onChange({ exceptionsOnly })}
                  />
                  <ToggleBox
                    id="availability-delivery"
                    label="Delivery roles only"
                    checked={view.deliveryOnly}
                    onChange={(deliveryOnly) => onChange({ deliveryOnly })}
                  />
                </div>
              </FilterSection>
              <FilterSection label="Hours">
                <div className="flex flex-col gap-2.5">
                  <ToggleBox
                    id="availability-proposed"
                    label="Include proposed"
                    checked={view.includeProposed}
                    onChange={(includeProposed) => onChange({ includeProposed })}
                  />
                  <ToggleBox
                    id="availability-actuals"
                    label="Show actual months"
                    checked={view.showActual}
                    onChange={(showActual) => onChange({ showActual })}
                  />
                </div>
              </FilterSection>
              <FilterSection label="Density">
                <ChipRow>
                  {(["comfortable", "compact"] as const).map((density) => (
                    <ChipToggle
                      key={density}
                      active={view.density === density}
                      onToggle={() => onChange({ density })}
                    >
                      {density === "comfortable" ? "Comfortable" : "Compact"}
                    </ChipToggle>
                  ))}
                </ChipRow>
              </FilterSection>
            </div>
          )}
        </FilterPopover>

        <Button
          type="button"
          variant="outline"
          className="ml-auto"
          onClick={onToggleAll}
          disabled={!canCollapse}
        >
          {allOpen ? "Collapse all" : "Expand all"}
        </Button>
      </div>

      {windowProblem !== null ? (
        <p role="alert" className="text-sm text-destructive">
          {windowProblem}. Showing Next 90 days instead.
        </p>
      ) : null}

      {chips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {chips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-sm font-medium text-foreground"
            >
              {c.label}
              <button
                type="button"
                onClick={c.clear}
                aria-label={`Remove ${c.label}`}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={clearAll}
            className="text-sm text-muted-foreground underline-offset-2 hover:underline"
          >
            Clear all
          </button>
        </div>
      ) : null}
    </section>
  );
}
